<!-- eslint-disable import-alias/import-alias -->
<script lang="ts" setup>
import { onBeforeMount, onMounted, onUnmounted, watch, inject, computed } from 'vue';
import { useNotificationStore } from '@/store/notificationStore';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useAnalytics } from '@/composables/useAnalytics';
import { useCookiebot } from '@/composables/useCookiebot';
import { useLanguage } from '@/composables/useLanguage';
import { useGtm } from '@gtm-support/vue-gtm';
import PostAuthGate from './views/newlogin/PostAuthGate.vue';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useLogin } from '@/login/composables/useLogin';
import { RouteNames } from './models/enums/RouteNames';
import { useUser } from '@/login/composables/useUser';
import { useTracking } from './composables/useTracking';
import { TrackingEvent } from './tracking/events';
import { useSatismeter } from './composables/useSatismeter';
import InstanceChangedWarning from './views/InstanceChangedWarning.vue';
import type { EventService } from './services/EventService';
import type { RightsService } from './services/RightsService';
import { EntityCache } from '@sysarb/sysarb-api/cache';
import { useOpentelemetry } from './composables/useOpentelemetry';
import { useTanstackCache } from './api/TanstackCache';
import ConsoleLoggerControlPanel from './components/ConsoleLoggerControlPanel.vue';
import { useSettings } from './login/composables/useSettings';

console.info({ cat: 'Global', msg: '-- DEV MODE ON --' });

useLogin();
useTanstackCache();

const loginStore = useLoginStore();
const { userData, isLoggedIn, isInstanceSelected, role, instancesLoading, resgateAuthenticated, instanceTopic, selectedInstance, lastAuthenticatedInstance, instance, showInstanceChangedWarning } = storeToRefs(loginStore);
const { isSupport, isActiveInstance, isConsultant } = useUser();
const notificationStore = useNotificationStore();
const { instanceSettingsMissing } = useSettings();
const { init: initAnalytics } = useAnalytics();
const { init: initTracking, identify: identifyUser, startSession: startTrackingSession, endSession: endTrackingSession, track } = useTracking();
const { init: initSatismeter } = useSatismeter();
const { init: initOpentelemetry } = useOpentelemetry();

const gtm = useGtm();
gtm?.debug(import.meta.env.DEV === true);

const router = useRouter();

const eventService = inject<EventService>('eventService');
const rightsService = inject<RightsService>('rightsService');

const { cookieBot } = useCookiebot(true);
const { language, updateLanguage } = useLanguage();

const requiresAuth = computed(() => router.currentRoute.value.meta.requiresAuth);

const loginFlowRouteNames: (string | symbol)[] = [
    RouteNames.Login,
    RouteNames.LoginPassword,
    RouteNames.LoginSsoSelect,
    RouteNames.LoginBankId
];

const onLoginFlow = computed(() => {
    const name = router.currentRoute.value.name;
    return name != null && loginFlowRouteNames.includes(name);
});

/**
 * Overlay shown once the user is authenticated but the instance is not yet
 * ready (no instance selected / still loading / settings missing). Stays
 * route-independent so it also covers authenticated deep links.
 */
const showInstanceGate = computed(() => {
    if (isLoggedIn.value === false) return false;
    if (requiresAuth.value === false && onLoginFlow.value === false) return false;
    if (isInstanceSelected.value === false) return true;
    if (instancesLoading.value > 0) return true;
    if (instanceSettingsMissing.value === true) return true;

    return false;
});

const isDev = computed(() => import.meta.env.DEV);

const showLoggedInView = computed(() => {
    if (requiresAuth.value === false) return true;
    if (instanceTopic.value == null) return false;
    if (userData.value == null) return false;
    if (selectedInstance.value == null) return false;
    if (instance.value !== lastAuthenticatedInstance.value) return false;
    if (resgateAuthenticated.value === false) return false;

    return true;
});

onBeforeMount(() => {
    notificationStore.clear();
});

onMounted((): void => {
    eventService?.startEventTimer();

    // remove loading screen when app is ready.
    setTimeout(
        () => {
            const loadingScreen = document.getElementById('app-loading-screen');
            if (loadingScreen != null) {
                document.body.removeChild(loadingScreen);
            }
        },
        1000
    );

    window.showDebug = gtm?.debug;
});

onUnmounted(() => {
    eventService?.endEventTimer();
});

watch(() => eventService?.subscriptions.value, (val) => {
    console.debug('EventService', 'Current subscriptions', val?.length);
}, { deep: true });

watch(() => cookieBot.value, () => {
    if (cookieBot.value.statistics) {
        initAnalytics();
        initTracking();
        initSatismeter();
        initOpentelemetry();
    }
}, { immediate: true, deep: true });

watch(() => language.value, () => {
    void updateLanguage(language.value);
}, {
    deep: true,
    immediate: true,
    once: true,
});

watch(() => role.value, () => {
    if (role.value == null) return;
    rightsService?.clearRights();
    EntityCache.clear();
});

watch(() => isLoggedIn.value, (loggedIn, wasLoggedIn) => {
    if (loggedIn) startTrackingSession();
    else if (wasLoggedIn === true) endTrackingSession();
}, { immediate: true });

watch(() => showLoggedInView.value, () => {
    if (showLoggedInView.value === false) return;
    identifyUser();
    track(TrackingEvent.LoggedIn, { role: role.value?.role });
}, { immediate: true });

watch(() => isActiveInstance.value, () => {
    if (showLoggedInView.value === false) return;
    if (isSupport.value === true) return;
    if (isConsultant.value === true) return;
    if (isActiveInstance.value === true) return;

    console.error('The instance is not active. Redirecting.', selectedInstance.value);
    loginStore.setInstance(null);
}, { immediate: true });

</script>

<template>
    <ConsoleLoggerControlPanel v-if="isDev" />
    <InstanceChangedWarning v-if="showInstanceChangedWarning" />
    <template v-else>
        <PostAuthGate v-if="showInstanceGate === true" />

        <router-view v-else-if="showLoggedInView" />
    </template>
</template>

<style lang="scss">
@import "./styles/app.scss";
</style>
